import { useState, useEffect } from "react";
import styles from "./AddElementModal.module.css";
import SearchableDropdown from "../../../SearchableDropdown/SearchableDropdown";
import Modal from "../../../Modals/Modal";
import { postUser } from "../../../../helper/http";

interface StepOption {
  id: string;
  name: string;
}

interface Primitive {
  id: number;
  name: string;
  action: string;
  params: any;
}

interface AddElementModalProps {
  isOpen: boolean;
  elementType: "step" | "event" | "primitive";
  steps?: StepOption[];
  selectedStepIndex?: number;
  onStepChange?: (index: number) => void;
  onClose: () => void;
  onAdd: (data: any) => void;
}

export default function AddElementModal({
  isOpen,
  elementType,
  steps,
  selectedStepIndex,
  onStepChange,
  onClose,
  onAdd,
}: AddElementModalProps) {
  const [formData, setFormData] = useState<any>({
    type: "primitive",
  });
  const [primitivesList, setPrimitivesList] = useState<Primitive[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && elementType === "primitive") {
      loadPrimitives();
    }
  }, [isOpen, elementType]);

  const loadPrimitives = async () => {
    try {
      setLoading(true);
      const response = await postUser("crud/list", {
        module: "primitive",
        page: "1",
        per_page: "100",
        sort_direction: "+",
        sort_field: "id",
      });

      if (response.data) {
        setPrimitivesList(response.data);
      }
    } catch (error) {
      console.error("Error loading primitives:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    onAdd(formData);
    setFormData({ type: "primitive" });
  };

  const getTitle = () => {
    switch (elementType) {
      case "step":
        return "Add Step";
      case "event":
        return "Add Event";
      case "primitive":
        return "Add Primitive / Process";
      default:
        return "Add Element";
    }
  };

  const renderStepSelector = () => {
    if (!steps || !onStepChange) return null;

    const stepOptions = steps.map((step, index) => ({
      label: step.name,
      value: index.toString()
    }));

    return (
      <div className={styles.formGroup}>
        <SearchableDropdown
          label="Add to Step"
          options={stepOptions}
          value={selectedStepIndex?.toString() || "0"}
          onChange={(val) => onStepChange(Number(val))}
        />
      </div>
    );
  };

  const renderStepForm = () => {
    const eventOptions = [
      { label: "Select event", value: "" },
      { label: "source_message", value: "source_message" },
      { label: "button_click", value: "button_click" },
      { label: "timer", value: "timer" },
      { label: "webhook", value: "webhook" },
    ];

    const fromStepOptions = steps
      ? steps.map((step, index) => ({
          label: step.name,
          value: `step${index + 1}`
        }))
      : [];

    return (
      <>
        <div className={styles.formGroup}>
          <SearchableDropdown
            label="Event Type"
            options={eventOptions}
            value={formData.event || ""}
            onChange={(val) =>
              setFormData({ ...formData, event: val })
            }
          />
        </div>

        {steps && steps.length > 0 && (
          <div className={styles.formGroup}>
            <label>From Steps (select multiple):</label>
            <div className={styles.checkboxGroup}>
              {fromStepOptions.map((option) => (
                <label key={option.value} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.from?.includes(option.value) || false}
                    onChange={(e) => {
                      const currentFrom = formData.from || [];
                      let newFrom;
                      if (e.target.checked) {
                        newFrom = [...currentFrom, option.value];
                      } else {
                        newFrom = currentFrom.filter((v: string) => v !== option.value);
                      }
                      setFormData({
                        ...formData,
                        from: newFrom.length > 0 ? newFrom : undefined
                      });
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className={styles.formGroup}>
          <label>Condition (JSON, optional):</label>
          <textarea
            rows={3}
            placeholder='{"$message.text$": "/start"}'
            value={
              formData.cond ? JSON.stringify(formData.cond, null, 2) : ""
            }
            onChange={(e) => {
              try {
                const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
                setFormData({ ...formData, cond: parsed });
              } catch {
                /* ignore invalid json */
              }
            }}
          />
        </div>

        <div className={styles.formGroup}>
          <label>Initial Primitive Type:</label>
          <SearchableDropdown
            label=""
            options={[
              { label: "Primitive", value: "primitive" },
              { label: "Process", value: "process" },
            ]}
            value={formData.primitiveType || "primitive"}
            onChange={(val) =>
              setFormData({ ...formData, primitiveType: val })
            }
          />
        </div>

        <div className={styles.formGroup}>
          <label>Initial Primitive/Process Name:</label>
          <input
            type="text"
            value={formData.primitiveName || ""}
            onChange={(e) =>
              setFormData({ ...formData, primitiveName: e.target.value })
            }
            placeholder="process_start"
          />
        </div>
      </>
    );
  };

  const renderEventForm = () => {
    const eventOptions = [
      { label: "Select event", value: "" },
      { label: "source_message", value: "source_message" },
      { label: "button_click", value: "button_click" },
      { label: "timer", value: "timer" },
      { label: "webhook", value: "webhook" },
    ];

    const fromStepOptions = steps
      ? steps.map((step, index) => ({
          label: step.name,
          value: `step${index + 1}`
        }))
      : [];

    return (
      <>
        <div className={styles.formGroup}>
          <SearchableDropdown
            label="Event Type"
            options={eventOptions}
            value={formData.event || ""}
            onChange={(val) =>
              setFormData({ ...formData, event: val })
            }
          />
        </div>

        {steps && steps.length > 0 && (
          <div className={styles.formGroup}>
            <label>From Steps (select multiple):</label>
            <div className={styles.checkboxGroup}>
              {fromStepOptions.map((option) => (
                <label key={option.value} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.from?.includes(option.value) || false}
                    onChange={(e) => {
                      const currentFrom = formData.from || [];
                      let newFrom;
                      if (e.target.checked) {
                        newFrom = [...currentFrom, option.value];
                      } else {
                        newFrom = currentFrom.filter((v: string) => v !== option.value);
                      }
                      setFormData({
                        ...formData,
                        from: newFrom.length > 0 ? newFrom : undefined
                      });
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className={styles.formGroup}>
          <label>Condition (JSON, optional):</label>
          <textarea
            rows={3}
            placeholder='{"$message.text$": "/start"}'
            value={
              formData.cond ? JSON.stringify(formData.cond, null, 2) : ""
            }
            onChange={(e) => {
              try {
                const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
                setFormData({ ...formData, cond: parsed });
              } catch {
                /* ignore invalid json */
              }
            }}
          />
        </div>
      </>
    );
  };

  const renderPrimitiveForm = () => {
    const typeOptions = [
      { label: "Primitive", value: "primitive" },
      { label: "Process", value: "process" },
    ];

    const primitiveOptions = [
      { label: `Select ${formData.type}`, value: "" },
      ...primitivesList.map(prim => ({
        label: prim.name,
        value: prim.action
      }))
    ];

    return (
      <>
        <div className={styles.formGroup}>
          <SearchableDropdown
            label="Type"
            options={typeOptions}
            value={formData.type}
            onChange={(val) =>
              setFormData({ ...formData, type: val })
            }
          />
        </div>

        <div className={styles.formGroup}>
          <SearchableDropdown
            label={formData.type === "process" ? "Process" : "Primitive"}
            options={primitiveOptions}
            value={
              formData.type === "process"
                ? formData.process || ""
                : formData.primitive || ""
            }
            onChange={(val) =>
              setFormData({
                ...formData,
                [formData.type === "process"
                  ? "process"
                  : "primitive"]: val,
              })
            }
            disabled={loading}
          />
          {loading && <div className={styles.loadingText}>Loading primitives...</div>}
        </div>

        <div className={styles.formGroup}>
          <label>Module ID (optional):</label>
          <input
            type="number"
            value={formData.moduleId ?? ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                moduleId: e.target.value
                  ? Number(e.target.value)
                  : undefined,
              })
            }
            placeholder="32"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Body (JSON, optional):</label>
          <textarea
            rows={3}
            placeholder='{"message_id": ""}'
            value={
              formData.body ? JSON.stringify(formData.body, null, 2) : ""
            }
            onChange={(e) => {
              try {
                const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
                setFormData({ ...formData, body: parsed });
              } catch {
                /* ignore invalid json */
              }
            }}
          />
        </div>

        <div className={styles.formGroup}>
          <label>Condition (JSON, optional):</label>
          <textarea
            rows={3}
            placeholder='{"process": {"$exists": true}}'
            value={
              formData.cond ? JSON.stringify(formData.cond, null, 2) : ""
            }
            onChange={(e) => {
              try {
                const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
                setFormData({ ...formData, cond: parsed });
              } catch {
                /* ignore invalid json */
              }
            }}
          />
        </div>
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={getTitle()}>
      <div className={styles.modalBody}>
        {(elementType === "primitive" || elementType === "event") && renderStepSelector()}

        {elementType === "primitive" && renderPrimitiveForm()}
        {elementType === "event" && renderEventForm()}
        {elementType === "step" && renderStepForm()}
      </div>

      <div className={styles.modalFooter}>
        <button
          className={styles.cancelButton}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className={styles.addButton}
          onClick={handleAdd}
        >
          Add
        </button>
      </div>
    </Modal>
  );
}